import type { MaybeRef } from '@vueuse/core';
import { get, useIntervalFn } from '@vueuse/core';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, nextTick, ref } from 'vue';
import { useIdle } from '@/composable/useIdle';
import { useState } from '@/composable/useState';
import { assignArray, remove, upsert } from '@/core/lib/array';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { formatOutrightLeagueEuro } from '@/core/oddsApi/helpers/formatOutrightLeague';
import { useOutrightTournamentsEuroQuery, useOutrightTournamentsEuroSubscription } from '@/core/oddsApi/oddsApiType';
import type { IOutrightLeagueEuro } from '@/interface/IOutrightLeague';
import { SportType } from '@/interface/enum';
import { useOddsApiStore } from '@/store/oddsApiStore';

interface IOutrightLeaguesParam {
    sportType: SportType;
    leagueIds?: MaybeRef<number[] | undefined>;
    leagueNames?: MaybeRef<string[] | undefined>;
    isActive?: Ref<boolean>;
}

export function useOutrightLeaguesEuro({ sportType, leagueIds, leagueNames, isActive = ref(true) }: IOutrightLeaguesParam) {
    const oddsApiSportType = SportType.toOddsApi(sportType);
    // sort here to generate a stable query
    const sortedLeagueIds = computed(() => {
        const _leagueIds = get(leagueIds);
        return _leagueIds ? orderBy(_leagueIds) : undefined;
    });
    const sortedLeagueNames = computed(() => {
        const _leagueNames = get(leagueNames);
        return _leagueNames ? orderBy(_leagueNames) : undefined;
    });

    const variables = computed(() => ({
        query: {
            sport: oddsApiSportType,
            tournamentNames: sortedLeagueNames.value,
            tournamentIds: sortedLeagueIds.value,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const { loaded, loading, onResult } = useOutrightTournamentsEuroQuery(
        variables,
        computed(() => ({
            enabled: isActive.value && isEnabled.value,
        })),
    );
    const { onSubscription } = useOutrightTournamentsEuroSubscription(
        variables,
        computed(() => ({
            enabled: isActive.value,
        })),
    );

    const outrightLeagues = ref<IOutrightLeagueEuro[]>([]);

    const { customerSettings } = storeToRefs(useOddsApiStore());
    onResult((result) => {
        const newOutrightLeagues = (result ?? [])
            .map(x => ({
                ...x,
                sport: {
                    sportType: oddsApiSportType,
                },
            }))
            .filter(x => BusinessLogicHelper.isOutrightTournamentAllowed(customerSettings.value, x))
            .map(x => deepFreeze(formatOutrightLeagueEuro(x, sportType)));
        assignArray(outrightLeagues.value, newOutrightLeagues, x => x.id);
    });

    onSubscription((subscription) => {
        const toUpdate = (subscription?.updated ?? [])
            .map(x => ({
                ...x,
                sport: {
                    sportType: oddsApiSportType,
                },
            }))
            .filter(x => BusinessLogicHelper.isOutrightTournamentAllowed(customerSettings.value, x))
            .map<IOutrightLeagueEuro>(x => deepFreeze(formatOutrightLeagueEuro(x, sportType)));

        upsert(outrightLeagues.value, toUpdate, item => item.id);
        remove(outrightLeagues.value, subscription?.deleted ?? [], (a, b) => a.id === b.id && a.isLive === b.isLive);
    });

    useIntervalFn(refetch, 120 * 1000);

    // Automatically recover from idle state with a force refresh
    // This is to prevent the user from losing data when they are idle
    const { onActive } = useIdle(60 * 1000);
    onActive(() => {
        // eslint-disable-next-line no-console
        console.log('useOutrightLeaguesEuro: Recovering from idle state...');
        refetch();
    });

    return {
        loaded,
        loading,
        refetch,
        outrightLeagues,
    };
}
